import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Search,
  Music,
  ExternalLink,
  FileText,
  Video,
  BookOpen,
  PlayCircle,
  Loader2,
  Music2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicSongsPage() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedSong, setExpandedSong] = useState(null);

  useEffect(() => {
    loadSongs();
  }, []);

  const loadSongs = async () => {
    try {
      const response = await fetch(`${API}/api/public/songs`);
      if (response.ok) {
        const data = await response.json();
        setSongs(data.songs || []);
      }
    } catch (error) {
      console.error("Error loading songs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSongs = songs.filter((song) => {
    const searchLower = search.toLowerCase();
    return (
      song.nome?.toLowerCase().includes(searchLower) ||
      song.autor?.toLowerCase().includes(searchLower) ||
      song.tom?.toLowerCase().includes(searchLower)
    );
  });

  const getLinkIcon = (type) => {
    switch (type) {
      case "video":
        return <Video className="h-4 w-4" />;
      case "cifra":
        return <BookOpen className="h-4 w-4" />;
      case "letra":
        return <FileText className="h-4 w-4" />;
      default:
        return <ExternalLink className="h-4 w-4" />;
    }
  };

  const openLink = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-white/70">Carregando músicas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/40 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2.5 rounded-xl">
              <Music2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Banco de Músicas</h1>
              <p className="text-sm text-white/60">{songs.length} músicas cadastradas</p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              placeholder="Buscar música, autor ou tom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-purple-400"
            />
          </div>
        </div>
      </div>

      {/* Songs List */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {filteredSongs.length === 0 ? (
          <div className="text-center py-12">
            <Music className="h-16 w-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">
              {search ? "Nenhuma música encontrada" : "Nenhuma música cadastrada"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSongs.map((song) => (
              <Card
                key={song.id}
                className="bg-white/5 border-white/10 hover:bg-white/10 transition-all cursor-pointer overflow-hidden"
                onClick={() => setExpandedSong(expandedSong === song.id ? null : song.id)}
              >
                <CardContent className="p-4">
                  {/* Main info */}
                  <div className="flex items-start gap-3">
                    <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 p-2 rounded-lg flex-shrink-0">
                      <Music className="h-5 w-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">{song.nome}</h3>
                      {song.autor && (
                        <p className="text-sm text-white/60 truncate">{song.autor}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {song.tom && (
                          <Badge variant="outline" className="text-xs border-purple-400/50 text-purple-300 bg-purple-500/10">
                            Tom: {song.tom}
                          </Badge>
                        )}
                        {song.bpm && (
                          <Badge variant="outline" className="text-xs border-blue-400/50 text-blue-300 bg-blue-500/10">
                            {song.bpm} BPM
                          </Badge>
                        )}
                        {song.capotraste && song.capotraste > 0 && (
                          <Badge variant="outline" className="text-xs border-amber-400/50 text-amber-300 bg-amber-500/10">
                            Capo {song.capotraste}
                          </Badge>
                        )}
                        {song.versoes && song.versoes.length > 0 && (
                          <Badge variant="outline" className="text-xs border-green-400/50 text-green-300 bg-green-500/10">
                            {song.versoes.length} versões
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded content */}
                  {expandedSong === song.id && (
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                      {/* Links */}
                      <div className="flex flex-wrap gap-2">
                        {song.link_video && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-400/50 text-red-300 hover:bg-red-500/20 bg-red-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              openLink(song.link_video);
                            }}
                          >
                            <PlayCircle className="h-4 w-4 mr-1.5" />
                            Vídeo
                          </Button>
                        )}
                        {song.link_cifra && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-400/50 text-blue-300 hover:bg-blue-500/20 bg-blue-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              openLink(song.link_cifra);
                            }}
                          >
                            <BookOpen className="h-4 w-4 mr-1.5" />
                            Cifra
                          </Button>
                        )}
                        {song.link_letra && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-400/50 text-green-300 hover:bg-green-500/20 bg-green-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              openLink(song.link_letra);
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1.5" />
                            Letra
                          </Button>
                        )}
                      </div>

                      {/* Versions */}
                      {song.versoes && song.versoes.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-white/50 uppercase tracking-wide">Versões disponíveis:</p>
                          <div className="grid gap-2">
                            {song.versoes.map((versao, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-2 rounded-lg bg-white/5"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-white">{versao.nome}</span>
                                  {versao.tom && (
                                    <Badge variant="outline" className="text-[10px] border-purple-400/50 text-purple-300">
                                      {versao.tom}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  {versao.link_video && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openLink(versao.link_video);
                                      }}
                                    >
                                      <PlayCircle className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  {versao.link_cifra && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openLink(versao.link_cifra);
                                      }}
                                    >
                                      <BookOpen className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  {versao.link_letra && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-green-400 hover:text-green-300 hover:bg-green-500/20"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openLink(versao.link_letra);
                                      }}
                                    >
                                      <FileText className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No links message */}
                      {!song.link_video && !song.link_cifra && !song.link_letra && (!song.versoes || song.versoes.length === 0) && (
                        <p className="text-sm text-white/40 text-center py-2">
                          Nenhum link disponível para esta música
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-8 text-white/30 text-xs">
        <p>Powered by 8 Life App</p>
      </div>
    </div>
  );
}
